import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All'); // 'All', 'New', 'Read', 'Resolved'

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('requests')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      alert('Failed to load requests.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('requests')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      // Update local state
      setRequests(requests.map(req => 
        req.id === id ? { ...req, status: newStatus } : req
      ));
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status.');
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request permanently?')) return;
    
    try {
      const { error } = await supabase
        .from('requests')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      setRequests(requests.filter(req => req.id !== id));
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Failed to delete request.');
    }
  };

  const filteredRequests = filter === 'All' 
    ? requests 
    : requests.filter(req => req.status === filter);

  const getStatusColor = (status) => {
    switch(status) {
      case 'New': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'Read': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'Contact Form': return 'mail';
      case 'Site Visit': return 'directions_car';
      case 'Brochure Download': return 'download';
      case 'FAQ Inquiry': return 'help';
      default: return 'chat';
    }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h2 className="text-3xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage Requests & Leads</h2>
        
        <div className="flex items-center gap-2 bg-[#1c1b1b] border border-white/10 rounded p-1 overflow-x-auto whitespace-nowrap">
          {['All', 'New', 'Read', 'Resolved'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${filter === f ? 'bg-[#f59e0b] text-[#2a1700]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#d8c3ad]">
          <span className="material-symbols-outlined animate-spin text-4xl mb-4">sync</span>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-[#1c1b1b] p-12 rounded-xl border border-white/10 text-center">
          <span className="material-symbols-outlined text-gray-600 text-5xl mb-4">inbox</span>
          <h3 className="text-xl text-gray-400 font-semibold mb-2">No Requests Found</h3>
          <p className="text-gray-500">You don't have any {filter.toLowerCase()} requests at the moment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map(req => (
            <div key={req.id} className="bg-[#1c1b1b] rounded-xl border border-white/10 p-6 flex flex-col lg:flex-row lg:items-start justify-between gap-6 hover:border-white/20 transition-colors">
              
              <div className="flex gap-4 flex-1">
                <div className="w-12 h-12 rounded-full bg-[#0e0e0e] border border-white/5 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[#d8c3ad]">{getTypeIcon(req.type)}</span>
                </div>
                
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <h4 className="text-lg font-bold text-white">{req.name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(req.status)}`}>{req.status}</span>
                    <span className="text-xs text-gray-500 bg-[#0e0e0e] px-2 py-0.5 rounded">{req.type}</span>
                    <span className="text-xs text-gray-600 flex items-center gap-1 ml-auto">
                      <span className="material-symbols-outlined text-[14px]">schedule</span>
                      {new Date(req.created_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mb-4 text-sm">
                    <a href={`mailto:${req.email}`} className="text-[#f59e0b] hover:underline flex items-center gap-1">
                      <span className="material-symbols-outlined text-[16px]">mail</span> {req.email}
                    </a>
                    <a href={`tel:${req.phone}`} className="text-[#f59e0b] hover:underline flex items-center gap-1">
                      <span className="material-symbols-outlined text-[16px]">call</span> {req.phone}
                    </a>
                  </div>

                  {req.message && (
                    <div className="bg-[#0e0e0e] border border-white/5 p-4 rounded text-sm text-gray-300 whitespace-pre-wrap">
                      {req.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2 border-t border-white/10 pt-4 lg:border-t-0 lg:pt-0 lg:pl-6 lg:border-l shrink-0">
                <select 
                  className="bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-[#f59e0b]"
                  value={req.status}
                  onChange={(e) => updateStatus(req.id, e.target.value)}
                >
                  <option value="New">Mark as New</option>
                  <option value="Read">Mark as Read</option>
                  <option value="Resolved">Mark as Resolved</option>
                </select>
                <button 
                  onClick={() => deleteRequest(req.id)}
                  className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 px-3 py-2"
                >
                  <span className="material-symbols-outlined text-[16px]">delete</span> Delete
                </button>
              </div>
              
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRequests;
